import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';

import { axiosInstance } from 'common/network';
import { AnalyticsToolbar, AnalyticsTable, AnalyticsError } from './components';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
  },
  content: {
    marginTop: theme.spacing(2),
  },
}));

const ROWS_PER_PAGE = 50;

// Only the text/uuid fields flow straight through; the date pickers are widened to
// full-day bounds so a single-day pick still matches events across that whole day.
const buildParams = (filters, page) => {
  const params = { page, pageSize: ROWS_PER_PAGE };
  ['accountEmail', 'profileEmail', 'accountUuid', 'type', 'key', 'applicationName', 'userAgent']
    .forEach(field => {
      const value = (filters[field] || '').trim();
      if (value) params[field] = value;
    });
  if (filters.from) params.from = moment(filters.from).startOf('day').toISOString();
  if (filters.to) params.to = moment(filters.to).endOf('day').toISOString();
  return params;
};

// A rejected request could be a real HTTP error, an auth failure that survived the
// interceptor's silent refresh, or an unreachable host — map each to a user-facing line.
const describeError = error => {
  const status = error && error.response && error.response.status;
  if (status === 401 || status === 403) {
    return 'You are not authorized to view analytics. Your session may have expired — try signing in again.';
  }
  if (status >= 500) {
    return 'The analytics service is temporarily unavailable. Please try again in a moment.';
  }
  if (status) {
    return `Failed to load analytics (HTTP ${status}).`;
  }
  return 'Could not reach the analytics service. Check your connection and try again.';
};

const AnalyticsList = () => {
  const classes = useStyles();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  // Bumped by the error banner's Retry so a failed fetch can be replayed without
  // otherwise changing the filters/page inputs.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    // Guards against an out-of-order response (fast filter/page changes) clobbering
    // the results of a later request.
    let cancelled = false;
    const params = buildParams(filters, currentPage + 1);

    setLoading(true);
    setError(null);
    axiosInstance
      .get(process.env.REACT_APP_API_ROOT + 'v3/analytics', { params })
      .then(response => {
        if (cancelled) return;
        const data = response.data || {};
        setItems(Array.isArray(data.items) ? data.items : []);
        setTotal(typeof data.total === 'number' ? data.total : 0);
        setLoading(false);
      })
      .catch(requestError => {
        if (cancelled) return;
        setItems([]);
        setTotal(0);
        setError(describeError(requestError));
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [filters, currentPage, reloadToken]);

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <div className={classes.root}>
        <AnalyticsToolbar
          onApply={applied => {
            setCurrentPage(0);
            setFilters(applied);
          }}
        />
        <div className={classes.content}>
          {error ? (
            <AnalyticsError message={error} onRetry={() => setReloadToken(token => token + 1)} />
          ) : (
            <AnalyticsTable
              loading={loading}
              items={items}
              total={total}
              page={currentPage}
              rowsPerPage={ROWS_PER_PAGE}
              onChangePage={setCurrentPage}
            />
          )}
        </div>
      </div>
    </MuiPickersUtilsProvider>
  );
};

export default AnalyticsList;
